/**
 * TV1-2291 — WHY a KPI tile has no number.
 *
 * `kpiData.ts` answers *what* the number is (and refuses to invent one). This
 * module answers the question the headline row actually fails at today: **why is
 * it missing?** On the ticket owner's account three of the seven headline tiles
 * show no value and four of the five that could show a delta show none, and the
 * tile renders all of it as one undifferentiated `—`. Four genuinely different
 * situations, one glyph.
 *
 * DOM-free, like its sibling: the *reason* a tile is empty is a semantic
 * question, and it is answered — and tested — without a renderer. Rendering is a
 * separate slice; this module decides, it does not draw.
 *
 * ## ⚠️ THE TRAP: Cube cannot tell you which of the two it is
 *
 * `prerequisite-missing` and `empty-period` **produce the identical response**.
 * Both are "the measure summed to unknown". Worse, the two obvious tie-breakers
 * do not work:
 *
 *  - **Row count is not a signal.** A dimensionless KPI query is a bare
 *    `SELECT SUM(…)`, which returns ONE row holding `NULL` over an empty window —
 *    the same one row a populated-window-but-unknown-measure query returns. A
 *    grouped query returns none. So `rows.length` distinguishes query *shapes*,
 *    not causes, and is deliberately not read here.
 *  - **"The cube has no rows for this account" is wrong for the most important
 *    tile.** The SDD proposes exactly that heuristic (§4b). It holds for
 *    `SellerReputation` and `Conversion`, whose cubes are genuinely empty until a
 *    sync/visit-reporting channel exists — but it is FALSE for
 *    `Sales.netMargin`, whose cube is full (tiles 1, 3 and 4 read it fine) and
 *    whose prerequisite is at the MEASURE level: `Sales.js` returns `NULL` for
 *    every margin measure until costs are imported, precisely so a missing cost
 *    cannot manufacture a ~100% margin. Cube-level emptiness would classify the
 *    single tile the complaint is most about as "no sales this month".
 *
 * So the discriminator does not read the response alone. Two mechanisms, in
 * strict precedence — **evidence beats declaration**:
 *
 * 1. **A witness measure (`activityMeasure`)** — a second measure IN THE SAME
 *    QUERY that is non-zero iff the window had activity (`Sales.orderCount` for
 *    the margin tile). Value unknown + witness ACTIVE = the window is not the
 *    problem, the prerequisite is. Value unknown + witness IDLE = the window
 *    genuinely had nothing, and that is a real answer, not a failure. This is the
 *    only branch that *measures* the difference rather than being told it.
 * 2. **A declared prerequisite (`prerequisite.message`)** — the author asserting
 *    "this metric cannot exist until X". Correct for the cube-level cases, where
 *    the metric can never be produced until setup happens, so the window is
 *    irrelevant.
 *
 * ## What is NOT distinguishable here, stated plainly
 *
 * **When the prerequisite's own signal is the thing being windowed, no in-window
 * evidence can separate the two causes.** `Conversion.conversionRatePct` is
 * `NULL` exactly when `visits = 0`, and `visits = 0` is equally consistent with
 * "no channel reports visits at all" (prerequisite) and "a visits-reporting
 * channel exists but this month was dead" (empty period). Every candidate witness
 * inside the window collapses to the same observation.
 *
 * Separating them needs an **unwindowed existence probe** — "has this account
 * EVER had a visits row / a reputation snapshot?" — which is a second query and
 * therefore not available to a pure function over one window's response. Until
 * such a signal exists, those tiles are classified from the author's declaration,
 * `basis: 'declaration'` records that it was asserted rather than measured, and
 * nothing here pretends otherwise.
 *
 * ## Precedence
 *
 * ```
 * no payload            -> loading            (absence is NOT emptiness)
 * fetch failed          -> error
 * value known           -> ready | no-prior-window   (the delta axis)
 * value unknown, witness ACTIVE -> prerequisite-missing (basis: witness)
 * value unknown, witness IDLE   -> empty-period        (basis: witness)
 * value unknown, no witness, prerequisite declared -> prerequisite-missing (declaration)
 * value unknown, no witness, nothing declared      -> empty-period        (default)
 * ```
 */

import { isCompareWidgetData, type CubeRow } from '../widgetData';
import { sanitizeKpiHref, shapeKpiData, sumMeasure, type KpiHref } from './kpiData';

/**
 * The author's answer to "what is missing, and what fixes it?".
 *
 * `message` is user-facing prose and is REQUIRED — a prerequisite state whose
 * whole purpose is to name the action is worthless without one, so a descriptor
 * without a message is not a prerequisite at all (see {@link parseKpiPrerequisite}).
 *
 * `link` is already scheme-allow-listed by `sanitizeKpiHref`: widget layouts are
 * shared across every user in the account, so an authored destination is the same
 * stored-XSS surface `kpi.href` was (TV1-1934). `null` means "render the sentence,
 * no anchor" — never a raw fallback.
 */
export type KpiPrerequisite = {
  message: string;
  link: KpiHref | null;
};

/** How a `prerequisite-missing` verdict was reached. Measured, or asserted. */
export type KpiPrerequisiteBasis = 'witness' | 'declaration';

/** How an `empty-period` verdict was reached. Measured, or the honest default. */
export type KpiEmptyPeriodBasis = 'witness' | 'default';

/**
 * Why the delta row is absent even though the value is present.
 *
 * Both render the same way — **the delta row is simply not there** (ADR-051's
 * "unknown is not zero" forbids fabricating one, and a placeholder `—` for it is
 * visual noise that reads as breakage). They are kept apart because they are
 * different facts: nobody ASKED for a comparison vs. there was nothing to compare
 * against. Tile 7 (seller reputation) is the first by construction — a snapshot
 * has no history.
 */
export type KpiNoPriorReason = 'no-compare-requested' | 'prior-window-empty';

/**
 * The four causes of a missing number, plus the two states where a number exists.
 *
 * A discriminated union rather than flags so a renderer CANNOT show "sin datos"
 * for a prerequisite: it has to handle each kind explicitly.
 */
export type KpiState =
  /**
   * No payload yet. The tile is waiting, not empty — and it must never flash an
   * "empty period" message while a fetch is in flight.
   */
  | { kind: 'loading' }
  /** The query failed (bad measure, 401). Distinct from all three empties. */
  | { kind: 'error'; message: string }
  /**
   * The metric cannot exist until something is set up.
   *
   * `prerequisite` is `null` when the WITNESS proved the window had activity but
   * the author never said what is missing: the state is measured, the sentence is
   * not authored. The renderer says "cannot be computed", not "no activity" —
   * claiming an empty period over a window with orders in it would be a lie.
   */
  | { kind: 'prerequisite-missing'; prerequisite: KpiPrerequisite | null; basis: KpiPrerequisiteBasis }
  /** The query is fine and the window is genuinely empty. A REAL ANSWER. */
  | { kind: 'empty-period'; basis: KpiEmptyPeriodBasis }
  /** The value exists; there is nothing to compare it against. Delta row absent. */
  | { kind: 'no-prior-window'; reason: KpiNoPriorReason }
  /** Value and delta both measured. */
  | { kind: 'ready' };

/**
 * What the resolver is handed. All three are available at the widget parser seam
 * (`WebWidget.parseKpiData` already receives payload + props + definition).
 */
export type KpiStateInput = {
  /**
   * The fetch outcome. Either the `WidgetDataState` envelope (`{ status: 'error' }`
   * / `{ status: 'ok', data }`) or the already-unwrapped payload — both are
   * accepted, because `WebWidget` unwraps before it reaches the parser while the
   * error branch never does. Pass the envelope if you want the `error` state.
   */
  data: unknown;
  /** The widget's single measure — `definition.measures[0]`. */
  measure: string;
  /** `definition.widgetComponentProps`. Free-form by schema; read defensively. */
  widgetComponentProps?: unknown;
};

/** Shown when the fetch failed without saying how. Never blank. */
const UNSPECIFIED_ERROR = 'The query failed';

/**
 * Whether the WINDOW had activity, according to the witness measure.
 *
 * `'unknown'` covers both "no witness configured" and "the witness is not in the
 * response" — the two are the same thing here: no evidence. It is NOT a third
 * verdict about the window.
 */
type KpiActivity = 'unknown' | 'idle' | 'active';

/**
 * Read the fetch-error envelope off an `unknown`.
 *
 * `isWidgetDataError` needs its argument pre-narrowed to `WidgetDataState`, which
 * at this seam means casting an `unknown` we have not checked; the message then
 * needs a second cast to read. A local structural check is honest about what it
 * knows and costs five lines. Keep it in step with `WidgetDataState`.
 */
const readFetchError = ( data: unknown ): string | null => {
  if ( typeof data !== 'object' || data === null ) return null;

  const envelope = data as { status?: unknown; error?: unknown };
  if ( envelope.status !== 'error' ) return null;

  return typeof envelope.error === 'string' && envelope.error ? envelope.error : UNSPECIFIED_ERROR;
};

/** Unwrap `{ status: 'ok', data }`; pass anything else through untouched. */
const unwrapPayload = ( data: unknown ): unknown => {
  if ( typeof data !== 'object' || data === null ) return data;

  const envelope = data as { status?: unknown; data?: unknown };
  return envelope.status === 'ok' ? envelope.data : data;
};

/**
 * The CURRENT window's rows, whichever shape the fetch produced.
 *
 * The witness is only ever read from the current window: it exists to describe
 * the window whose value is missing.
 */
const currentRows = ( payload: unknown ): CubeRow[] | null => {
  if ( isCompareWidgetData( payload )) return payload.current;
  return Array.isArray( payload ) ? ( payload as CubeRow[]) : null;
};

const asRecord = ( raw: unknown ): Record<string, unknown> | null =>
  typeof raw === 'object' && raw !== null ? ( raw as Record<string, unknown> ) : null;

const asNonEmptyString = ( raw: unknown ): string | null =>
  typeof raw === 'string' && raw.trim() ? raw.trim() : null;

/**
 * The witness measure, off `widgetComponentProps.activityMeasure`.
 *
 * Deliberately a SIBLING of `prerequisite`, not a member of it: evidence and copy
 * are independent. A tile can measure that its window had activity without its
 * author having written the explanatory sentence yet, and a tile can carry the
 * sentence with no witness available.
 *
 * ⚠️ The named measure must be IN THE QUERY (`definition.measures`) or there is
 * nothing to read. Note the manual builder's `validateKpi` currently rejects a
 * second measure ("A KPI shows exactly one measure"); adopting a witness on an
 * author-editable tile needs that rule relaxed to "exactly one DISPLAYED measure".
 */
export const readKpiActivityMeasure = ( widgetComponentProps: unknown ): string | null => {
  const props = asRecord( widgetComponentProps );
  return props ? asNonEmptyString( props.activityMeasure ) : null;
};

/**
 * The PARTIAL-COVERAGE witness measure, off `widgetComponentProps.partialCoverageMeasure`.
 *
 * TV1-2392 (slice 6) — a SEPARATE declaration from {@link readKpiActivityMeasure},
 * and deliberately NOT the positional `definition.measures[1]`. `measures[1]` is
 * this repo's generic ACTIVITY-witness slot: four curated tiles (`kpi-net-revenue`,
 * `kpi-net-margin`, `kpi-avg-ticket`, `kpi-return-rate`) already put `Sales.orderCount`
 * / `ReturnRate.soldUnits` there. Reading coverage positionally therefore fired the
 * stock-umbral disclosure on every healthy tile with orders in the window — the
 * witness being POSITIVE is the normal case for an activity witness and the alarm
 * case for a coverage witness. The two meanings must be named, not inferred from a
 * slot, so a tile opts in by declaring the measure explicitly.
 *
 * ⚠️ The named measure must also be IN THE QUERY (`definition.measures`); the
 * curated critical-stock KPI declares it in BOTH roles (activity + coverage), which
 * is what keeps `hasUnjustifiedKpiMeasures` satisfied while both mechanisms work.
 */
export const readKpiPartialCoverageMeasure = ( widgetComponentProps: unknown ): string | null => {
  const props = asRecord( widgetComponentProps );
  return props ? asNonEmptyString( props.partialCoverageMeasure ) : null;
};

/**
 * Validate the authored prerequisite descriptor.
 *
 * `null` — never a half-built object — when there is no usable message: the
 * prerequisite state exists to NAME the missing action ("Importá costos para ver
 * la ganancia"), and a state that names nothing is just a dash with extra steps.
 * A rejected `href` degrades to `link: null` and keeps the sentence; it never
 * falls back to a raw target.
 */
export const parseKpiPrerequisite = ( raw: unknown ): KpiPrerequisite | null => {
  const candidate = asRecord( raw );
  if ( !candidate ) return null;

  const message = asNonEmptyString( candidate.message );
  if ( !message ) return null;

  return { message, link: sanitizeKpiHref( candidate.href ) };
};

/** Classify the window from the witness measure. See {@link KpiActivity}. */
const readActivity = ( rows: CubeRow[] | null, activityMeasure: string | null ): KpiActivity => {
  if ( !activityMeasure ) return 'unknown';

  const witness = sumMeasure( rows, activityMeasure );
  if ( witness === null ) return 'unknown';

  return witness === 0 ? 'idle' : 'active';
};

/**
 * Decide why a KPI tile looks the way it does.
 *
 * Pure. One window's response plus the widget's own definition; no fetching, no
 * rendering, no clock.
 */
export const resolveKpiState = ({ data, measure, widgetComponentProps }: KpiStateInput ): KpiState => {
  // Absence is not emptiness. A tile that has not been answered yet must not
  // render "sin ventas en este período" — that is a claim about the data.
  if ( data === undefined || data === null ) return { kind: 'loading' };

  const fetchError = readFetchError( data );
  if ( fetchError ) return { kind: 'error', message: fetchError };

  const payload = unwrapPayload( data );
  const kpi = shapeKpiData( payload, measure );

  const props = asRecord( widgetComponentProps );
  // Same rule as the props builder: anything that is not the literal `'text'` is
  // a number, so a typo'd mode cannot silently switch which field is authoritative.
  const isTextMode = props?.valueMode === 'text';

  // "Known" must mean exactly what `formatKpiValue` will print — a non-finite
  // number renders as the em-dash, so treating it as a value here would produce a
  // `ready` tile showing "—", i.e. the wall of dashes this module exists to end.
  const hasValue = isTextMode
    ? kpi.textValue !== null
    : kpi.value !== null && Number.isFinite( kpi.value );

  if ( hasValue ) {
    // The value carries the tile. A declared prerequisite is IGNORED here on
    // purpose: if the number arrived, the prerequisite is evidently satisfied,
    // whatever the definition still says.
    if ( kpi.delta.kind !== 'unknown' ) return { kind: 'ready' };

    return {
      kind: 'no-prior-window',
      reason: kpi.hasCompare ? 'prior-window-empty' : 'no-compare-requested'
    };
  }

  const activity = readActivity( currentRows( payload ), readKpiActivityMeasure( widgetComponentProps ));
  const prerequisite = parseKpiPrerequisite( props?.prerequisite );

  // ── Evidence first ────────────────────────────────────────────────────────
  // The window had activity and the measure is still unknown: the period is not
  // the reason. This is the `Sales.netMargin` case — orders exist, costs do not.
  if ( activity === 'active' ) {
    return { kind: 'prerequisite-missing', prerequisite, basis: 'witness' };
  }

  // The window had NO activity, measured. This OVERRIDES a declared prerequisite:
  // "no sales this month" is the true and more useful answer even for a tile that
  // also needs a cost import. A measurement outranks an assertion.
  if ( activity === 'idle' ) {
    return { kind: 'empty-period', basis: 'witness' };
  }

  // ── No evidence: fall back to what the author declared ────────────────────
  if ( prerequisite ) {
    return { kind: 'prerequisite-missing', prerequisite, basis: 'declaration' };
  }

  // Nothing measured, nothing declared. The honest default is the one that claims
  // least: we ran the query and the window was empty.
  return { kind: 'empty-period', basis: 'default' };
};
